import React from 'react';
import { Checkbox, DatePicker, Form, Input, Select, Tooltip, Row, Col } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';
import Button from 'antd/lib/button';

import DictField, { Label, StyledQuestion, ListWrapper, ErrorWrapper } from './DictField';
import SecretField, { StyledFormItem } from './SecretField/SecretField';
import { Secret } from '../../../../../types.generated';
import { RecipeField, FieldType } from './common';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { getTranslatedPlaceholder, translateRulesMessage } from '../../utils';

const StyledRemoveIcon = styled(MinusCircleOutlined)`
    font-size: 14px;
    margin-left: 10px;
`;

const StyledSpan = styled.span`
    color: #ff4d4f;
    margin-left: 4px;
`;

interface CommonFieldProps {
    field: RecipeField;
    removeMargin?: boolean;
}

interface AntSelectOption {
    children?: React.ReactNode;
    value: string;
    key: string;
    tabIndex?: number;
}

function ListField({ field, removeMargin }: CommonFieldProps) {
    const { t } = useTranslation();

    return (
        <Form.List name={field.name} rules={field.rules || undefined}>
            {(fields, { add, remove }, { errors }) => (
                <ListWrapper removeMargin={!!removeMargin}>
                    <Label>
                        {t(field.label)}
                        {field.required && <StyledSpan>*</StyledSpan>}
                        <Tooltip overlay={t(field.tooltip as string)}>
                            <StyledQuestion />
                        </Tooltip>
                    </Label>
                    {fields.map((item) => (
                        <Form.Item key={item.fieldKey} style={{ marginBottom: '10px' }}>
                            <Form.Item {...item} noStyle>
                                <Input style={{ width: '80%' }} placeholder={t(field.placeholder || '')} />
                            </Form.Item>
                            <StyledRemoveIcon onClick={() => remove(item.name)} />
                        </Form.Item>
                    ))}
                    <Button type="default" onClick={() => add()} icon={<PlusOutlined />} aria-label={field.buttonLabel}>
                        {t(field.buttonLabel || '')}
                    </Button>
                    <ErrorWrapper>{errors}</ErrorWrapper>
                </ListWrapper>
            )}
        </Form.List>
    );
}

function MultiListField({ field, removeMargin }: CommonFieldProps) {
    const { t } = useTranslation();

    function createDefaultValues(configs = []) {
        return configs.reduce(
            (defaults, { name }) => ({
                ...defaults,
                [name]: '',
            }),
            {},
        );
    }

    return (
        <Form.List name={field.name} rules={field.rules || undefined}>
            {(fields, { add, remove }) => (
                <ListWrapper removeMargin={!!removeMargin}>
                    <Label>
                        {t(field.label)}
                        {field.required && <StyledSpan>*</StyledSpan>}
                        <Tooltip overlay={t(field.tooltip as string)}>
                            <StyledQuestion />
                        </Tooltip>
                    </Label>
                    {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={16} className="mb-3" align="middle">
                            <Col span={20}>
                                {(field.configs || []).map((config) => (
                                    <Form.Item
                                        {...restField}
                                        key={config?.fieldKey}
                                        name={[name, config.name]}
                                        rules={config.rules}
                                        className="mb-1"
                                        required={config.required}
                                    >
                                        <Input
                                            placeholder={t(config.placeholder || '')}
                                            data-testid={`${field.name}-${config.name}-${name}`}
                                        />
                                    </Form.Item>
                                ))}
                            </Col>
                            <StyledRemoveIcon onClick={() => remove(name)} />
                        </Row>
                    ))}
                    <Form.Item>
                        <Button
                            type="dashed"
                            onClick={() => add(createDefaultValues(field.configs))}
                            block
                            icon={<PlusOutlined />}
                        >
                            {t(field.buttonLabel || '')}
                        </Button>
                    </Form.Item>

                    <ErrorWrapper>
                        {!fields.length && field.required && t('Please provide at least one item.')}
                    </ErrorWrapper>
                </ListWrapper>
            )}
        </Form.List>
    );
}

function SelectField({ field, removeMargin }: CommonFieldProps) {
    const { t } = useTranslation();

    return (
        <StyledFormItem
            required={field.required}
            name={field.name}
            label={t(field.label)}
            tooltip={t(field.tooltip as string)}
            removeMargin={!!removeMargin}
            rules={translateRulesMessage(field.rules || [], t(field.label))}
        >
            {field.options && (
                <Select
                    aria-label={t(field.label || '')}
                    placeholder={t(field.placeholder || '')}
                    allowClear={!field.required}
                    disabled={field?.disabled || false}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                >
                    {field.options.map((option) => (
                        <Select.Option value={option.value} tabIndex={0} key={option.value}>
                            {t(option.label)}
                        </Select.Option>
                    ))}
                </Select>
            )}
        </StyledFormItem>
    );
}

function SearchSelectField({ field, removeMargin }: CommonFieldProps) {
    const { t } = useTranslation();
    const filterOption = (input: string, option?: AntSelectOption) => {
        return option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false;
    };

    return (
        <StyledFormItem
            required={field.required}
            name={field.name}
            label={t(field.label)}
            tooltip={t(field.tooltip as string)}
            removeMargin={!!removeMargin}
            rules={field.rules || undefined}
        >
            {field.options && (
                <Select
                    aria-label={field.label || ''}
                    placeholder={t(field.placeholder || '')}
                    allowClear={!field.required}
                    showSearch
                    filterOption={filterOption}
                    disabled={field?.disabled || false}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode} // Control dropdown container
                >
                    {field.options.map((option) => (
                        <Select.Option value={option.value} tabIndex={0} key={option.value}>
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            )}
        </StyledFormItem>
    );
}

function DateField({ field, removeMargin }: CommonFieldProps) {
    const { t } = useTranslation();

    return (
        <StyledFormItem
            required={field.required}
            name={field.name}
            label={t(field.label)}
            tooltip={t(field.tooltip as string)}
            removeMargin={!!removeMargin}
            rules={field.rules || undefined}
        >
            <DatePicker showTime />
        </StyledFormItem>
    );
}

interface Props {
    field: RecipeField;
    secrets: Secret[];
    refetchSecrets: () => void;
    removeMargin?: boolean;
    updateFormValue: (field, value) => void;
}

function FormField(props: Props) {
    const { t } = useTranslation();
    const { field, secrets, refetchSecrets, removeMargin, updateFormValue } = props;

    const fieldComponentMap = {
        [FieldType.LIST]: () => <ListField field={field} removeMargin={removeMargin} />,
        [FieldType.MULTI_LIST]: () => <MultiListField field={field} removeMargin={removeMargin} />,
        [FieldType.SELECT]: () => <SelectField field={field} removeMargin={removeMargin} />,
        [FieldType.SEARCH_SELECT]: () => <SearchSelectField field={field} removeMargin={removeMargin} />,
        [FieldType.DATE]: () => <DateField field={field} removeMargin={removeMargin} />,
        [FieldType.SECRET]: () => (
            <SecretField
                field={field}
                secrets={secrets}
                removeMargin={removeMargin}
                refetchSecrets={refetchSecrets}
                updateFormValue={updateFormValue}
            />
        ),
        [FieldType.DICT]: () => <DictField field={field} />,
    };

    const renderComponent = fieldComponentMap[field.type];
    if (renderComponent) {
        return renderComponent();
    }

    // Default case for TEXT, BOOLEAN, TEXTAREA
    const isBoolean = field.type === FieldType.BOOLEAN;
    let input = <Input placeholder={getTranslatedPlaceholder(field.placeholder || '')} />;
    if (isBoolean) input = <Checkbox />;
    if (field.type === FieldType.TEXTAREA)
        input = <Input.TextArea required={field.required} placeholder={t(field.placeholder || '')} />;

    const valuePropName = isBoolean ? 'checked' : 'value';
    const getValueFromEvent = isBoolean ? undefined : (e) => (e.target.value === '' ? '' : e.target.value);

    return (
        <StyledFormItem
            name={field.name}
            label={t(field.label || '')}
            alignLeft={isBoolean}
            tooltip={t(field.tooltip as string)}
            required={field.required}
            removeMargin={!!removeMargin}
            valuePropName={valuePropName}
            rules={translateRulesMessage(field.rules || [], t(field.label))}
            getValueFromEvent={getValueFromEvent}
            className={isBoolean ? 'f-checkbox-wrapper' : ''}
        >
            {input}
        </StyledFormItem>
    );
}

export default FormField;
